import React, { useEffect, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";

import { Button, InfoMessage, MeshColours, MeshGradient, Modal, TicketFormCard, TicketNumberSelector, TitleSubtitle, TicketFormCardProps, Tooltip } from "../../../../../components";
import { TicketContainer, StyledForm, StyledBlueSpan } from "./TicketsStep.styles";
import { ButtonContainer, ButtonIcon, ButtonLabel, CTAButtonContainer } from "../../BookingManager.styles";

import { IBookingTicket, IUserBookingTicket } from "../../../../../models/BookingTicket";
import { UserInterface } from "../../../../../models/User";
import { emailValidation } from "../../../../../utils/regex";
import { Modals, useModal } from "../../../../../contexts/ModalContext";
import { usePriorityAllocationStore } from "../../../../../store/usePriorityAllocationStore";

interface TicketsStepProps extends WithRouterProps {
  showId?: number;
  onNextButtonClick: Function;
  onPrevButtonClick: Function;
  user: UserInterface | null | undefined;
  minAge?: number;
  isPriorityActive?: boolean;
  sessionId?: number;
}

const colorArray: MeshColours[] = [
  "orange-pink",
  "pink-blue-teal",
  "blue-green-teal",
  "teal-green",
  "red-green-teal",
  "pink-purple-green",
];

const TicketsStep: React.FC<TicketsStepProps> = ({
  showId,
  onNextButtonClick,
  onPrevButtonClick,
  user,
  router,
  minAge,
  isPriorityActive,
  sessionId
}) => {
  const { openModal, closeModal } = useModal();
  const getAvailablePrioritySeats = usePriorityAllocationStore((state) => state.getAvailablePrioritySeats);
  const [ticketNumber, setTicketNumber] = React.useState(1);
  const [genericError, setGenericError] = useState(false);
  const [nextDisabled, setNextDisabled] = React.useState(true);
  const emptyBookingTicket = {
    firstName: "",
    lastName: "",
    email: "",
    age: 0,
    accessibility: '',
    otherAccessibility: null
  } as IBookingTicket;

  const ticketFormCardRef = React.useRef<TicketFormCardProps>(null);

  const triggerValidation = () => {
    if (ticketFormCardRef.current && ticketFormCardRef.current.validate) {
      ticketFormCardRef.current.validate();
    }
  };

  const [userBookingTicket, setUserBookingTicket] =
    useState<IUserBookingTicket>({
      bookForAnotherPerson: false,
      loggedInUserTicket: {
        firstName: user?.firstName!,
        lastName: user?.lastName!,
        email: user?.email!,
        age: user?.age!,
        accessibility: user?.accessibility!,
        otherAccessibility: user?.otherAccessibility!
      },
      ticketForAnotherPerson: [],
      error: false,
    });

  useEffect(() => {

    const formValues = sessionStorage.getItem('formValues');
    const prePopulateForm = formValues ? JSON.parse(formValues) : null;

    setUserBookingTicket({
      ...userBookingTicket,
      ticketForAnotherPerson: prePopulateForm == null ? [] : prePopulateForm
    });

    const ticketNumberStored = sessionStorage.getItem('ticketNumber');
    const prePopulateTicketNumber = ticketNumberStored ? JSON.parse(ticketNumberStored) : null;

    if (prePopulateTicketNumber !== null) {
      setTicketNumber(Number(prePopulateTicketNumber));
      if (Number(prePopulateTicketNumber) == 1) {
        setNextDisabled(false)
      }
    } else {
      setNextDisabled(false)
    }

    validateTicketHolders(prePopulateForm)
  }, []);

  const handleChangeTicketNumber = (ticketNumber: number) => {
    setTicketNumber(ticketNumber)
    let otherTickets = JSON.parse(JSON.stringify(userBookingTicket.ticketForAnotherPerson));

    if (ticketNumber == 1) {
      otherTickets = [],
        setNextDisabled(false)
    } else {
      if (otherTickets.length >= ticketNumber) {
        otherTickets = otherTickets.slice(0, ticketNumber - 1)
      }
      if (otherTickets.length < ticketNumber - 1) {
        while (otherTickets.length < ticketNumber - 1) {
          otherTickets.push(emptyBookingTicket);
        }
      }
    }

    setUserBookingTicket({
      ...userBookingTicket,
      loggedInUserTicket: userBookingTicket.loggedInUserTicket,
      ticketForAnotherPerson: otherTickets
    });

    validateTicketHolders(otherTickets)
  }

  const handleFormChange = (ticket: IBookingTicket, index: number) => {
    let currentObjects = userBookingTicket.ticketForAnotherPerson;

    if (currentObjects?.length > 0) {
      currentObjects[index] = ticket;

      validateTicketHolders(currentObjects);

      setUserBookingTicket({
        ...userBookingTicket,
        ticketForAnotherPerson: currentObjects
      });
    }
  };

  const validateTicketHolders = (object: any) => {
    for (let i in object) {
      if (!(object[i].firstName == "" ||
        object[i].lastName == "" ||
        (object[i].age >= 18 && (object[i].email == "" || !emailValidation(object[i].email) || userBookingTicket.loggedInUserTicket.email === object[i].email)) ||
        object[i].age == 0 ||
        object[i].accessibility == "")) {
        setNextDisabled(false);
        setGenericError(false)
      } else {
        setNextDisabled(true);
        break
      }
    }
  }

  const handleNextButtonClick = () => {
    sessionStorage.setItem("formValues", JSON.stringify(userBookingTicket.ticketForAnotherPerson));
    sessionStorage.setItem("ticketNumber", JSON.stringify(ticketNumber));
    if (isPriorityActive && sessionId) {
      const availableSeats = getAvailablePrioritySeats(sessionId);
      if (availableSeats !== undefined && ticketNumber > availableSeats) {
        openModal(Modals.NotEnoughPriorityTickets, () => { closeModal(); onNextButtonClick(false); });
        return;
      }
    }
    onNextButtonClick(isPriorityActive);
  }

  const handleDisabledClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation;

    if (nextDisabled) {
      setGenericError(true);
      triggerValidation();
    } else {
      setGenericError(false);
      handleNextButtonClick();
    }
  };


  return (
    <TicketContainer data-cy="ticketContainer">
      <TitleSubtitle
        title={"How many tickets?"}
        subTitle={<>Please make sure you have the <Tooltip tooltipName="correctEmail"><StyledBlueSpan> correct email address </StyledBlueSpan></Tooltip>of any guest over 18. If you don't know their age it can be an estimate for now.</>}
      />
      <TicketNumberSelector
        size={6}
        //TTT-795: Enable 6 instead of 4 tickets on Idol only for PROD
        maxTicketNumber={showId == 10 ? 6 : 4}
        defaultTicketNumber={ticketNumber}
        onTicketNumberChange={handleChangeTicketNumber}
      />
      <TicketFormCard
        index={-1}
        key={'userTicket-main'}
        ticketNumber={1}
        disabled={true}
        disabledIconTooltip="editLocked"
        bookingTicket={userBookingTicket.loggedInUserTicket}
        isPriority={isPriorityActive}
        colour={isPriorityActive ? "strong-gold" : undefined}
      />
      <StyledForm>
        {(userBookingTicket.ticketForAnotherPerson).map((otherTicket, index) =>
          <TicketFormCard
            data-cy="interest-form-row"
            key={`otherTickets-${index}`}
            index={index}
            bookingTicket={otherTicket}
            ticketNumber={index + 2}
            colour={isPriorityActive ? "strong-gold" : colorArray[index % colorArray.length]}
            disabled={false}
            handleFormChange={(ticket: IBookingTicket, index: number) => handleFormChange(ticket, index)}
            isPriority={isPriorityActive}
            minAge={minAge}
            loggedInUserEmail={userBookingTicket.loggedInUserTicket.email}
            ref={ticketFormCardRef}
          />
        )}
        <CTAButtonContainer>
          <Button
            size="md"
            variant="tertiary"
            onClick={() => onPrevButtonClick()}
            data-cy="button-booking-previous"
            sx={{ padding: 0 }}
          >
            <ButtonContainer>
              <ButtonIcon icon="ArrowLeft" />
              <ButtonLabel>Back</ButtonLabel>
            </ButtonContainer>
          </Button>
          <div onClick={handleDisabledClick}>
            <Button
              size="md"
              gradientStyle="gradient3"
              disabled={nextDisabled}
              data-cy="button-booking-next"
            >
              Apply for {ticketNumber} Ticket{ticketNumber == 1 ? '' : 's'}
            </Button>
          </div>
        </CTAButtonContainer>

        {genericError && (
          <InfoMessage hasBackground variant="error" message="Hang fire!! Please address the issues highlighted above before continuing 👆" />
        )}
      </StyledForm>
      <MeshGradient sx={{ position: 'fixed' }} colour={"teal-green"} position="bottom" size="low" />
      <Modal />
    </TicketContainer>
  );
};

export default withRouter(TicketsStep);
